import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import httpClient from '../_util/api';
import { setUserDetail, showLoader, hideLoader } from '../Store/mainSlice';
import { getRedirectPath } from '../Helpers/Redirect';
import { clearAuthSession, logoutIfSessionExpired, setAuthSessionExpiry } from '../_util/session';

/**
 * Guards role routes after hydrating user from API on refresh (Redux is empty).
 * Avoids redirecting to "/" then dashboard on every refresh.
 */
const RoleProtectedRoute = ({ role, element }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userdetail = useSelector((state) => state.mainSlice.userdetail);
  const [hydrated, setHydrated] = useState(!!userdetail?.role);
  const [authorized, setAuthorized] = useState(userdetail?.role === role);

  useEffect(() => {
    logoutIfSessionExpired();
    const token = localStorage.getItem('authToken');
    if (!token) {
      setHydrated(true);
      setAuthorized(false);
      return;
    }

    // Already have matching role in Redux (e.g. just logged in)
    if (userdetail?.role === role) {
      setHydrated(true);
      setAuthorized(true);
      return;
    }

    // Token but wrong/missing role in Redux — hydrate before deciding
    let cancelled = false;
    (async () => {
      try {
        dispatch(showLoader());
        const res = await httpClient.get('/user/getUserDetails');
        if (cancelled) return;
        if (res?.status === 200 && res.data?.token) {
          localStorage.setItem('authToken', res.data.token);
          setAuthSessionExpiry();
        }
        if (res?.status === 200 && res.data?.role) {
          dispatch(setUserDetail(res.data));
          setAuthorized(res.data.role === role);
        } else {
          setAuthorized(false);
        }
      } catch {
        clearAuthSession();
        setAuthorized(false);
      } finally {
        if (!cancelled) {
          dispatch(hideLoader());
          setHydrated(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [role, dispatch]);

  if (!hydrated) {
    return null; // loader shown via Redux; optional: return a spinner
  }

  const token = localStorage.getItem('authToken');
  if (!token) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!authorized) {
    const currentRole = userdetail?.role;
    if (currentRole && currentRole !== role) {
      return <Navigate to={getRedirectPath(currentRole)} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return element;
};

export default RoleProtectedRoute;
